import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import classNames from "classnames";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalBody,
  Button,
} from "reactstrap";
import {
  useGetDealDetail,
  useDeleteDeal,
  useUpdateDeal,
} from "../../api/deals/service/index";
import { useDeleteWorksheet } from "api/worksheet/service";
import { useGetAccount } from "api/account/service";
import { getActiveColorStyle } from "../../utils/colorUtils";
import { useTranslation } from "react-i18next";
import DealInfoCards from "views/components/Deals/DealInfoCards";
import FlagTab from "views/inventory/tabs/FlagTab";
import FilesTab from "views/inventory/tabs/FilesTab";
import UserDefinedTab from "views/inventory/tabs/UserDefinedTab";
import DealDetailTab from "views/components/Deals/DealDetailTab";
import NotesTab from "views/inventory/tabs/NotesTab";
import DealInsuranceTab from "views/components/Deals/DealInsuranceTab";
import DealApplicantTab from "views/components/Deals/DealApplicantTab";
import DealWholesalerTab from "views/components/Deals/DealWholesalerTab";
import DealWorksheetsTab from "views/components/Deals/DealWorksheetsTab";
import DealTradeInTab from "views/components/Deals/DealTradeInTab";
import { FooterButton } from "components/Footer/components";
import { BUTTON_STYLES, DRAWER_STYLES } from "components/Footer/constants";
import { MdDelete, MdOutlineArticle, MdSettings, MdPrint, MdSend, MdMoreHoriz } from "react-icons/md";
import DealsRightDrawer from "views/deals/components/DealsRightDrawer";
import InventoryDetail from "views/inventory/InventoryDetail";
import SubHeader from "components/Navbars/SubHeader";
import { useClearListStateOnLeave, LIST_STATE_FLOWS } from "utils/listStateSessionStorage";

const DEALS_LIST_STATE_KEY = "dealertiq-deals-list-state";

const DealsDetail = ({ activeColor = "blue", isLightTheme = true, shellThemeVariant = "1" }, ref) => {
  const { t } = useTranslation();
  const { id } = useParams();
  useClearListStateOnLeave(DEALS_LIST_STATE_KEY, LIST_STATE_FLOWS.deals);
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantId = searchParams.get("tenantId");
  const worksheetId = useMemo(
    () => searchParams.get("worksheet"),
    [searchParams]
  );
  const [activeTab, setActiveTab] = useState("DETAIL");
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const deleteDealMutation = useDeleteDeal();
  const deleteWorksheetMutation = useDeleteWorksheet();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteWorksheetModalOpen, setDeleteWorksheetModalOpen] = useState(false);
  const { data: account } = useGetAccount();
  const userDefinedTabRef = useRef();
  const updateDealMutation = useUpdateDeal();
  const [showDesiredVehicleModal, setShowDesiredVehicleModal] = useState(false);
  // Get active color style from utility function
  const activeColorStyle = useMemo(
    () => getActiveColorStyle(activeColor),
    [activeColor]
  );
  const [actionsClosed, setActionsClosed] = useState(false);
  const dealsDrawerRef = useRef(null);
  const [hoveredFooterButton, setHoveredFooterButton] = useState(null);
  const hasWorksheetParam = Boolean(worksheetId);
  const getFooterButtonStyle = useCallback(
    (buttonId) => {
      const isHovered = hoveredFooterButton === buttonId;

      const baseStyle = {
        ...BUTTON_STYLES.base,
        backgroundColor: "#181b23",
        borderColor: "#fff",
        borderWidth: "1px",
        borderStyle: "solid",
        marginLeft: "4px",
        transform: "scale(1) rotate(0deg)",
        boxShadow: "none",
        filter: "none",
        zIndex: "auto",
      };


      const hoverStyle = isHovered
        ? {
          ...BUTTON_STYLES.hovered,
          backgroundColor: activeColorStyle.background,
          borderColor: activeColorStyle.borderColor,
          borderWidth: "3px",
          transform: "scale(1.25)",
          boxShadow:
            "0 8px 25px rgba(0, 0, 0, 0.5), 0 0 20px rgba(255, 255, 255, 0.3)",
          filter: "brightness(1.2) contrast(1.1)",
          zIndex: 1000,
        }
        : {};

      return {
        ...baseStyle,
        ...hoverStyle,
      };
    },
    [activeColorStyle, hoveredFooterButton]
  );

  useEffect(() => {
    if (worksheetId) {
      setActiveTab("WORKSHEETS");
    }
  }, [worksheetId]);

  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isWorksheetEditing, setIsWorksheetEditing] = useState(false);
  const [selectedWorksheetId, setSelectedWorksheetId] = useState(null);

  // Tab refs
  const dealDetailTabRef = useRef();
  const dealInsuranceTabRef = useRef();
  const applicantTabRef = useRef();
  const wholesalerTabRef = useRef();
  const vehicleTabRef = useRef();
  const pricingTabRef = useRef();
  const documentsTabRef = useRef();
  const worksheetsTabRef = useRef();
  const tradeInTabRef = useRef();

  const [dealMainData, setDealMainData] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const tabScrollRef = useRef(null);
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);
  const isTheme2 = shellThemeVariant === "2";
  const useHorizontalMobileTabs = isMobile;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { data, isLoading, isFetching } = useGetDealDetail(id || "", activeTab);


  useEffect(() => {
    if (data && !initialDataLoaded) {
      setInitialDataLoaded(true);
    }
  }, [data, initialDataLoaded]);

  useEffect(() => {
    if (data) {
      if (!dealMainData) {
        setDealMainData(data);
      } else {
        setDealMainData((prev) => ({ ...prev, ...data }));
      }
    }
  }, [data]);



  // State for tabs to prevent re-rendering
  const [tabs, setTabs] = useState([
    "DETAIL",
    "APPLICANT",
    "INSURANCE",
    "WORKSHEETS",
    "FILES",
    "ECABINET",
    "USER DEFINED",
    "NOTES",
    "FLAGS",
  ]);
  const [tabsInitialized, setTabsInitialized] = useState(false);

  // Set tabs once based on deal_type
  useEffect(() => {
    if (data?.deal_type && !tabsInitialized) {
      const isWholesaleDeal = data.deal_type === "wholesale_deal";

      if (isWholesaleDeal) {
        setTabs([
          "DETAIL",
          "WHOLESALER",
          "WORKSHEETS",
          "FILES",
          "ECABINET",
          "USER DEFINED",
          "NOTES",
          "FLAGS",
        ]);
      } else {
        setTabs([
          "DETAIL",
          "APPLICANT",
          "INSURANCE",
          "WORKSHEETS",
          "FILES",
          "ECABINET",
          "USER DEFINED",
          "NOTES",
          "FLAGS",
        ]);
      }
      setTabsInitialized(true);
    }
  }, [data?.deal_type, tabsInitialized]);

  // Dynamically ensure trade-in tabs are listed if data arises
  useEffect(() => {
    setTabs((prevTabs) => {
      let newTabs = [...prevTabs];
      if (data?.trade_in_1 && !newTabs.includes("TRADE-IN-1")) {
        const idx = newTabs.findIndex(t => t === "APPLICANT" || t === "WHOLESALER");
        newTabs.splice(idx + 1, 0, "TRADE-IN-1");
      }
      if (data?.trade_in_2 && !newTabs.includes("TRADE-IN-2")) {
        const idx = newTabs.indexOf("TRADE-IN-1");
        if (idx !== -1) {
          newTabs.splice(idx + 1, 0, "TRADE-IN-2");
        } else {
          const idx2 = newTabs.findIndex(t => t === "APPLICANT" || t === "WHOLESALER");
          newTabs.splice(idx2 + 1, 0, "TRADE-IN-2");
        }
      }
      if (newTabs.length !== prevTabs.length) return newTabs;
      return prevTabs;
    });
  }, [data?.trade_in_1, data?.trade_in_2]);

  const getTabDisplayName = (tab) => {
    const tabNames = {
      DETAIL: t("dealsDetail.detail"),
      APPLICANT: t("dealsDetail.applicant"),
      INSURANCE: t("dealsDetail.insurance"),
      WHOLESALER: t("dealsDetail.wholesaler"),
      WORKSHEETS: t("dealsDetail.worksheets"),
      FILES: t("dealsDetail.files"),
      ECABINET: t("dealsDetail.ecabinet"),
      "USER DEFINED": t("dealsDetail.userDefined"),
      NOTES: t("dealsDetail.notes"),
      FLAGS: t("dealsDetail.flags"),
      "TRADE-IN-1": "TRADE-IN-1",
      "TRADE-IN-2": "TRADE-IN-2",
    };
    return tabNames[tab] || tab;
  };

  // Fiyatı dolar formatında gösteren yardımcı fonksiyon
  const formatUSD = useCallback((value) => {
    if (value === 0 || value === "0") return "$0";
    if (!value) return "";
    const num =
      typeof value === "string"
        ? parseFloat(value.replace(/[^\d.\-]/g, ""))
        : value;
    if (isNaN(num)) return "";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(num);
  }, []);

  // Deal yaşını hesapla - sadece bir kez hesapla
  const [dealAge, setDealAge] = useState(undefined);
  const [daysInDeal, setDaysInDeal] = useState("-");

  // İlk veri yüklendiğinde dealAge ve daysInDeal'i hesapla
  useEffect(() => {
    if (data && !dealAge && data.createdAt) {
      const dealDate = new Date(data.createdAt);
      const calculatedDealAge = Math.floor(
        (new Date() - dealDate) / (1000 * 60 * 60 * 24)
      );
      setDealAge(calculatedDealAge);
    }
  }, [data?.createdAt, dealAge]);

  useEffect(() => {
    if (data && daysInDeal === "-" && data.createdAt) {
      const calculatedDaysInDeal = Math.floor(
        (new Date() - new Date(data.createdAt)) / (1000 * 60 * 60 * 24)
      );
      setDaysInDeal(calculatedDaysInDeal);
    }
  }, [data?.createdAt, daysInDeal]);

  // Deal ID değiştiğinde değerleri sıfırla
  useEffect(() => {
    setDealAge(undefined);
    setDaysInDeal("-");
  }, [id]);

  // setShowMoreDetails fonksiyonunu optimize et
  const handleShowMoreDetails = useCallback(() => {
    setShowMoreDetails((prev) => !prev);
  }, []);

  const handleTabChange = useCallback(
    (newTab) => {
      setActiveTab(newTab);

      if (newTab !== "WORKSHEETS" && searchParams.has("worksheet")) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("worksheet");
        setSearchParams(newSearchParams, { replace: true });
        setIsWorksheetEditing(false);
        setSelectedWorksheetId(null);
      }
    },
    [searchParams, setSearchParams]
  );

  const checkTabScroll = useCallback(() => {
    const el = tabScrollRef.current;
    if (!el) {
      setCanScrollTabsRight(false);
      return;
    }
    setCanScrollTabsRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    if (!useHorizontalMobileTabs) {
      setCanScrollTabsRight(false);
      return undefined;
    }
    const runCheck = () => {
      requestAnimationFrame(checkTabScroll);
    };
    runCheck();
    const scrollEl = tabScrollRef.current;
    scrollEl?.addEventListener("scroll", checkTabScroll);
    window.addEventListener("resize", runCheck);
    return () => {
      scrollEl?.removeEventListener("scroll", checkTabScroll);
      window.removeEventListener("resize", runCheck);
    };
  }, [checkTabScroll, tabs.length, id, useHorizontalMobileTabs]);

  const handleTabScrollRight = useCallback(() => {
    if (tabScrollRef.current && canScrollTabsRight) {
      tabScrollRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  }, [canScrollTabsRight]);

  const handleRemoveClick = useCallback(() => {
    setDeleteWorksheetModalOpen(true);
  }, []);

  const selectedWorksheet = useMemo(() => {
    const worksheetsData = dealMainData?.worksheets;
    if (!worksheetsData) {
      return null;
    }
    const worksheetsArray = Array.isArray(worksheetsData)
      ? worksheetsData
      : [worksheetsData];

    if (worksheetId) {
      const match = worksheetsArray.find(
        (item) =>
          String(item?.worksheet_id ?? item?.id ?? item?.documentId ?? "") ===
          String(worksheetId)
      );
      if (match) {
        return match;
      }
      return null;
    }

    return worksheetsArray[0] || null;
  }, [dealMainData?.worksheets, worksheetId]);

  const contractDealSummary = useMemo(() => {
    const contractDate =
      selectedWorksheet?.contract_date ||
      selectedWorksheet?.contractDate ||
      dealMainData?.contract_date ||
      null;
    const lender =
      selectedWorksheet?.lender_name ||
      selectedWorksheet?.lender?.name ||
      selectedWorksheet?.lender ||
      dealMainData?.lender?.name ||
      dealMainData?.lender ||
      "";
    const salesPrice =
      selectedWorksheet?.sales_price ??
      selectedWorksheet?.cash_price ??
      selectedWorksheet?.price ??
      dealMainData?.sales_price ??
      dealMainData?.cash_price ??
      null;
    const vehicle =
      selectedWorksheet?.inventory?.vin ??
      "";

    return {
      contractDate,
      lender,
      salesPrice,
      vehicle,
    };
  }, [selectedWorksheet, dealMainData]);


  const decisionHistory = useMemo(() => {
    const worksheetHistory =
      selectedWorksheet?.decision_history ||
      selectedWorksheet?.decision_log ||
      selectedWorksheet?.decisions;

    if (Array.isArray(worksheetHistory)) {
      return worksheetHistory;
    }

    if (Array.isArray(dealMainData?.decision_history)) {
      return dealMainData.decision_history;
    }

    return [];
  }, [selectedWorksheet, dealMainData]);

  const navigateToWorksheetsTab = useCallback(() => {
    handleTabChange("WORKSHEETS");
  }, [handleTabChange]);

  const handleAddTradeIn = useCallback(() => {
    setTabs((prevTabs) => {
      if (!prevTabs.includes("TRADE-IN-1")) {
        const idx = prevTabs.findIndex(t => t === "APPLICANT" || t === "WHOLESALER");
        const newTabs = [...prevTabs];
        newTabs.splice(idx + 1, 0, "TRADE-IN-1");
        return newTabs;
      }
      return prevTabs;
    });
    handleTabChange("TRADE-IN-1");
  }, [handleTabChange]);

  const handleAddTradeIn2 = useCallback(() => {
    setTabs((prevTabs) => {
      if (!prevTabs.includes("TRADE-IN-2")) {
        const idx = prevTabs.indexOf("TRADE-IN-1");
        const newTabs = [...prevTabs];
        if (idx !== -1) {
          newTabs.splice(idx + 1, 0, "TRADE-IN-2");
        } else {
          const idx2 = prevTabs.findIndex(t => t === "APPLICANT" || t === "WHOLESALER");
          newTabs.splice(idx2 + 1, 0, "TRADE-IN-2");
        }
        return newTabs;
      }
      return prevTabs;
    });
    handleTabChange("TRADE-IN-2");
  }, [handleTabChange]);

  const handleAddDesiredVehicle = useCallback(() => {
    setShowDesiredVehicleModal(true);
  }, []);

  const handleDesiredVehicleAdded = useCallback(async (vehicles) => {
    if (vehicles && vehicles.length > 0) {
      const vehicle = vehicles[0];
      try {
        await updateDealMutation.mutateAsync({
          id,
          data: {
            desired_vehicle: {
              documentId: vehicle.documentId
            }
          }
        });
        setShowDesiredVehicleModal(false);
      } catch (e) {
        console.error(e);
      }
    }
  }, [id, updateDealMutation]);

  const handleRemoveDesiredVehicle = useCallback(async () => {
    if (window.confirm("Are you sure you want to remove the Desired Vehicle?")) {
      try {
        await updateDealMutation.mutateAsync({
          id,
          data: {
            desired_vehicle: null
          }
        });
      } catch (e) {
        console.error(e);
      }
    }
  }, [id, updateDealMutation]);

  const handleRemoveTradeIn1 = useCallback(async () => {
    if (data?.trade_in_1) {
      if (window.confirm("Are you sure you want to remove Trade In 1?")) {
        try {
          await updateDealMutation.mutateAsync({ id, data: { trade_in_1: null } });
          setTabs(prev => prev.filter(t => t !== "TRADE-IN-1"));
          if (activeTab === "TRADE-IN-1") handleTabChange("DETAIL");
        } catch (e) { console.error(e); }
      }
    } else {
      setTabs(prev => prev.filter(t => t !== "TRADE-IN-1"));
      if (activeTab === "TRADE-IN-1") handleTabChange("DETAIL");
    }
  }, [data, id, updateDealMutation, activeTab, handleTabChange]);

  const handleRemoveTradeIn2 = useCallback(async () => {
    if (data?.trade_in_2) {
      if (window.confirm("Are you sure you want to remove Trade In 2?")) {
        try {
          await updateDealMutation.mutateAsync({ id, data: { trade_in_2: null } });
          setTabs(prev => prev.filter(t => t !== "TRADE-IN-2"));
          if (activeTab === "TRADE-IN-2") handleTabChange("DETAIL");
        } catch (e) { console.error(e); }
      }
    } else {
      setTabs(prev => prev.filter(t => t !== "TRADE-IN-2"));
      if (activeTab === "TRADE-IN-2") handleTabChange("DETAIL");
    }
  }, [data, id, updateDealMutation, activeTab, handleTabChange]);

  const openDealsDrawer = useCallback((type) => {
    setHoveredFooterButton(null);
    if (
      dealsDrawerRef.current &&
      typeof dealsDrawerRef.current.open === "function"
    ) {
      dealsDrawerRef.current.open(type);
    }
  }, []);

  // Delete handlers
  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteDealMutation.mutateAsync({
        id: data?.deal_id,
      });
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting deal:", error);
      // Error handling is now done in the mutation hook
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
  };

  const handleConfirmWorksheetDelete = () => {
    if (!selectedWorksheet?.documentId) return;

    const targetId = selectedWorksheet?.documentId;


    deleteWorksheetMutation.mutate({
      id: targetId,
      dealId: id
    }, {
      onSuccess: () => {
        setDeleteWorksheetModalOpen(false);
        setIsWorksheetEditing(false);
        setSelectedWorksheetId(null);

        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('worksheet');
        setSearchParams(newSearchParams, { replace: true });
      },
      onError: (error) => {
        console.error("Error deleting worksheet:", error);
      }
    });
  };

  const handleCancelWorksheetDelete = () => {
    setDeleteWorksheetModalOpen(false);
  };

  // Save fonksiyonu
  const handleSave = async () => {
    setIsSaving(true);
    try {
      let result = { success: false };

      // Aktif tab'a göre save işlemi yap
      if (activeTab === "DETAIL" && dealDetailTabRef.current) {
        result = await dealDetailTabRef.current.handleSave();
      }
      if (activeTab === "INSURANCE" && dealInsuranceTabRef.current) {
        result = await dealInsuranceTabRef.current.handleSave();
      }
      if (activeTab === "APPLICANT" && applicantTabRef.current) {
        result = await applicantTabRef.current.handleSave();
      }
      if (activeTab === "WHOLESALER" && wholesalerTabRef.current) {
        result = await wholesalerTabRef.current.handleSave();
      }
      if (activeTab === "VEHICLE" && vehicleTabRef.current) {
        result = await vehicleTabRef.current.handleSave();
      }
      if (activeTab === "PRICING" && pricingTabRef.current) {
        result = await pricingTabRef.current.handleSave();
      }
      if (activeTab === "USER DEFINED" && userDefinedTabRef.current) {
        result = await userDefinedTabRef.current.handleSave();
      }
      if (activeTab === "DOCUMENTS" && documentsTabRef.current) {
        result = await documentsTabRef.current.handleSave();
      }
      if (activeTab === "TRADE-IN-1" && tradeInTabRef.current) {
        result = await tradeInTabRef.current.handleSave();
      }
      // Diğer tab'lar için de benzer işlemler eklenebilir
    } catch (error) {
      console.log("DealsDetail - error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // İlk yükleme sırasında loading göster
  if (isLoading && !initialDataLoaded) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "200px" }}
      >
        <div
          className="spinner-border"
          role="status"
          style={{ color: activeColorStyle.borderColor }}
        >
          <span className="sr-only">{t("dealsDetail.loading")}</span>
        </div>
        <span className="ml-2" style={{ color: activeColorStyle.borderColor }}>
          {t("dealsDetail.loadingDealData")}
        </span>
      </div>
    );
  }

  return (
    <div className="content" style={{ minHeight: "80vh", paddingTop: isMobile ? "7rem" : null }}>
      <div
        style={{width: "100%", }}
      >
        <SubHeader
          activeColor={activeColor}
          back={-1}
          backFallback="/deals"
          title={activeTab === "WORKSHEETS" && worksheetId 
            ? `Deal ${dealMainData?.deal_id || id} / Worksheet ${worksheetId}`
            : `Deal ${dealMainData?.deal_id || id}`}
          status={dealMainData?.deal_status}
          statusColor={dealMainData?.deal_status === "working" ? "#0092e0" : dealMainData?.deal_status === "prospect" ? "#16A34A" : dealMainData?.deal_status === "contracted" ? "#b98f06" :dealMainData?.deal_status === "spot_delivered" ? "#9d6413" :dealMainData?.deal_status === "cancelled" ? "#db0000" : dealMainData?.deal_status === "contracted_booked" ? "#624E88" : "#dc3545"}
          theme2ScrollGapFill={shellThemeVariant === "2"}
          isLightTheme={isLightTheme}
        />

        {/* Üstteki Dört Kutu - Memoized Component */}
        <div
          className={classNames("deal-detail-info-cards", {
            "deal-detail-info-cards--theme2": isTheme2,
          })}
        >
          <DealInfoCards
            isLightTheme={isLightTheme}
            isTheme2={isTheme2}
            alwaysExpanded={isTheme2}
            data={dealMainData}
            activeColor={activeColor}
            showMoreDetails={showMoreDetails}
            setShowMoreDetails={handleShowMoreDetails}
            getActiveColorStyle={() => activeColorStyle}
            formatUSD={formatUSD}
            dealAge={dealAge}
            daysInDeal={daysInDeal}
          />
        </div>

        {/* Tab Menüsü ve İçerik - Ayrı Bölüm */}
        <div
          className={
            useHorizontalMobileTabs
              ? "w-100 deal-detail-tabs detail-mobile-tabs"
              : "w-100 deal-detail-tabs"
          }
        >
          <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between">
            {useHorizontalMobileTabs ? (
              <div className="d-flex align-items-center flex-grow-1 min-width-0 w-100">
                <div
                  ref={tabScrollRef}
                  className="flex-grow-1 channel-tabs-scroll"
                  style={{ minWidth: 0 }}
                >
                  <Nav
                    tabs
                    className="border-0"
                    style={{
                      borderBottom: "none",
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "nowrap",
                      minWidth: "max-content",
                      width: "auto",
                      paddingLeft: 0,
                      paddingRight: 0,
                    }}
                  >
                    {tabs.map((tab) => (
                      <NavItem key={tab} style={{ flex: "0 0 auto" }}>
                        <NavLink
                          href="#"
                          className={
                            activeTab === tab
                              ? "active font-weight-bold"
                              : "text-muted"
                          }
                          style={{
                            background: "none",
                            border: "none",
                            position: "relative",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            color:
                              activeTab === tab
                                ? activeColorStyle.borderColor
                                : undefined,
                            fontWeight: activeTab === tab ? 700 : 500,
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            handleTabChange(tab);
                          }}
                        >
                          {getTabDisplayName(tab)}
                          {activeTab === tab && (
                            <div
                              style={{
                                height: 3,
                                background: activeColorStyle.borderColor,
                                position: "absolute",
                                left: 0,
                                right: 0,
                                bottom: -8,
                              }}
                            />
                          )}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>
                </div>
                {canScrollTabsRight && (
                  <button
                    type="button"
                    className="btn btn-link p-0 flex-shrink-0"
                    onClick={handleTabScrollRight}
                    style={{
                      color: activeColorStyle.borderColor,
                      lineHeight: 1,
                    }}
                    aria-label={t("common.scrollTabs")}
                  >
                    <i className="tim-icons icon-minimal-right" style={{ fontSize: 20 }} />
                  </button>
                )}
              </div>
            ) : (
              <Nav tabs className="border-0" style={{ borderBottom: "none" }}>
                {tabs.map((tab) => (
                <NavItem key={tab}>
                  <NavLink
                    href="#"
                    className={
                      activeTab === tab
                        ? "active font-weight-bold"
                        : "text-muted"
                    }
                    style={{
                      background: "none",
                      border: "none",
                      // fontSize: 16,
                      // marginRight: 8,
                      position: "relative",
                      // fontWeight: 500,
                      cursor: "pointer",
                      color:
                        activeTab === tab
                          ? activeColorStyle.borderColor
                          : undefined,
                      fontWeight: activeTab === tab ? 700 : 500,
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      handleTabChange(tab);
                    }}
                  >
                    {getTabDisplayName(tab)}
                    {activeTab === tab && (
                      <div
                        style={{
                          height: 3,
                          background: activeColorStyle.borderColor,
                          position: "absolute",
                          left: 0,
                          right: 0,
                          bottom: -8,
                        }}
                      />
                    )}
                  </NavLink>
                </NavItem>
              ))}
              </Nav>
            )}

            {/* Desktop View Save Button */}
            {account.permissions.deals_update &&
              (activeTab === "DETAIL" ||
                activeTab === "INSURANCE" ||
                activeTab === "APPLICANT" ||
                activeTab === "WHOLESALER" ||
                activeTab === "TRADE-IN-1" ||
                activeTab === "USER DEFINED") && (
                <div className="d-none d-lg-flex align-items-center">
                  <button
                    className="btn btn-sm d-inline-flex align-items-center justify-content-center"
                    style={{ ...activeColorStyle, color: "white", margin: 0 }}
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm mr-2"
                          role="status"
                          aria-hidden="true"
                        />
                        {t("dealsDetail.saving")}
                      </>
                    ) : (
                      t("dealsDetail.save")
                    )}
                  </button>
                </div>
              )}

            {/* Mobile View Save Button */}
            {account.permissions.deals_update &&
              (activeTab === "DETAIL" ||
                activeTab === "INSURANCE" ||
                activeTab === "APPLICANT" ||
                activeTab === "WHOLESALER" ||
                activeTab === "TRADE-IN-1" ||
                activeTab === "USER DEFINED") && (
                <div className="d-flex d-lg-none flex-column w-100 mt-2">
                  <button
                    className="btn btn-sm w-100 d-inline-flex align-items-center justify-content-center"
                    style={{ ...activeColorStyle, color: "white", padding: '12px', margin: 0 }}
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm mr-2"
                          role="status"
                          aria-hidden="true"
                        />
                        {t("dealsDetail.saving")}
                      </>
                    ) : (
                      t("dealsDetail.save")
                    )}
                  </button>
                </div>
              )}
          </div>
          <TabContent activeTab={activeTab}>
            <TabPane tabId={activeTab}>
              <>
                {activeTab === "DETAIL" && (
                  <DealDetailTab
                    activeColor={activeColor}
                    ref={dealDetailTabRef}
                    data={data}
                    isTheme2={isTheme2}
                  />
                )}
                {activeTab === "INSURANCE" && (
                  <DealInsuranceTab
                    activeColor={activeColor}
                    ref={dealInsuranceTabRef}
                    data={data?.insurance}
                    id={id}
                    isTheme2={isTheme2}
                  />
                )}
                {activeTab === "APPLICANT" && (
                  <DealApplicantTab
                    tab={activeTab}
                    ref={applicantTabRef}
                    data={data}
                    id={id}
                    activeColor={activeColor}
                    isLoading={isLoading}
                    isTheme2={isTheme2}
                  />
                )}
                {activeTab === "WHOLESALER" && (
                  <DealWholesalerTab
                    tab={activeTab}
                    ref={wholesalerTabRef}
                    data={data}
                    id={id}
                    activeColor={activeColor}
                    isLoading={isLoading}
                    isTheme2={isTheme2}
                  />
                )}
                {activeTab === "WORKSHEETS" && (
                  <DealWorksheetsTab
                    dealId={id}
                    permission={account?.permissions}
                    ref={worksheetsTabRef}
                    data={data?.worksheets}
                    id={id}
                    activeColor={activeColor}
                    isWorksheetEditing={isWorksheetEditing}
                    setIsWorksheetEditing={setIsWorksheetEditing}
                    selectedWorksheetId={selectedWorksheetId}
                    setSelectedWorksheetId={setSelectedWorksheetId}
                    isTheme2={isTheme2}
                  />
                )}
                {activeTab === "TRADE-IN-1" && (
                  data?.trade_in_1 ? (
                    <div className="card">
                      <div className="card-header">
                        <h4 className="mb-0" style={{ fontSize: "1rem" }}>
                          <a
                            href={`/inventory/${data.trade_in_1.documentId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                              color: "inherit",
                            }}
                          >
                            {data.trade_in_1.vin}
                          </a>
                        </h4>
                      </div>
                      <div className="card-body mt-0 pt-0">
                        <InventoryDetail
                          inventoryId={data.trade_in_1.documentId}
                          isEmbedded={true}
                          activeColor={activeColor}
                          shellThemeVariant={shellThemeVariant}
                        />
                      </div>
                    </div>
                  ) : (
                    <DealTradeInTab
                      ref={tradeInTabRef}
                      id={id}
                      activeColor={activeColor}
                      isTheme2={isTheme2}
                    />
                  )
                )}
                {activeTab === "TRADE-IN-2" && (
                  data?.trade_in_2 ? (
                    <div className="card">
                      <div className="card-header">
                        <h4 className="mb-0" style={{ fontSize: "1rem" }}>
                          <a
                            href={`/inventory/${data.trade_in_2.documentId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                              color: "inherit",
                            }}
                          >
                            {data.trade_in_2.vin}
                          </a>
                        </h4>
                      </div>
                      <div className="card-body mt-0 pt-0">
                        <InventoryDetail
                          inventoryId={data.trade_in_2.documentId}
                          isEmbedded={true}
                          activeColor={activeColor}
                          shellThemeVariant={shellThemeVariant}
                        />
                      </div>
                    </div>
                  ) : null
                )}
                {activeTab === "FILES" && (
                  <FilesTab
                    module="deals"
                    type="file"
                    permission={{
                      add_files: account?.permissions?.deals_files_add,
                      delete_files: account?.permissions?.deals_files_add,
                    }}
                    id={data?.id}
                    data={data?.files}
                    isLoading={isLoading}
                    activeColor={activeColor}
                  />
                )}
                {activeTab === "ECABINET" && (
                  <FilesTab
                    module="deals"
                    type="ecabinet"
                    permission={{
                      add_files: account?.permissions?.deals_ecabinet_add,
                      delete_files: account?.permissions?.deals_ecabinet_add,
                    }}
                    id={data?.id}
                    data={data?.ecabinets}
                    isLoading={isLoading}
                    activeColor={activeColor}
                  />
                )}
                {activeTab === "USER DEFINED" && (
                  <UserDefinedTab
                    permission={account?.permissions}
                    data={data?.user_defineds}
                    activeColor={activeColor}
                    id={data?.documentId}
                    module="deals"
                    ref={userDefinedTabRef}
                  />
                )}
                {activeTab === "FLAGS" && (
                  <FlagTab
                    update={updateDealMutation}
                    module="deals"
                    permission={account?.permissions}
                    flags={data?.flags}
                    id={id}
                    activeColor={activeColor}
                    isTheme2={isTheme2}
                  />
                )}
                {activeTab === "NOTES" && (
                  <NotesTab
                    module="deals"
                    id={dealMainData?.id}
                    permission={{
                      notes_add: account?.permissions?.deals_notes_add,
                    }}
                    activeColor={activeColor}
                    isTheme2={isTheme2}
                  />
                )}
              </>
            </TabPane>
          </TabContent>
        </div>

        {/* Remove Button - Bottom of Screen */}
        {account?.permissions?.deals_delete &&
          (activeTab === "GENERAL" || activeTab === "CUSTOMER") && (
            <div className="d-flex justify-content-center mt-4 mb-4">
              <button
                className="btn btn-sm"
                style={{
                  background:
                    "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
                  backgroundImage:
                    "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
                  borderColor: "#fd5d93",
                  color: "white",
                }}
                onClick={handleDeleteClick}
                disabled={deleteDealMutation.isPending}
              >
                {deleteDealMutation.isPending ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm mr-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    {t("dealsDetail.removing")}
                  </>
                ) : (
                  t("dealsDetail.remove")
                )}
              </button>
            </div>
          )}
      </div>

      <div style={DRAWER_STYLES.floatingContainer}>
        {!actionsClosed && (
          <>
            {/* Worksheet-specific buttons - only show when worksheet is open */}
            {hasWorksheetParam && (
              <>
                <FooterButton
                  id="deals-drawer"
                  icon={<MdOutlineArticle size={20} color="#fff" />}
                  onClick={() => openDealsDrawer("deals")}
                  onHoverChange={(isHovered) =>
                    setHoveredFooterButton(isHovered ? "deals" : null)
                  }
                  style={getFooterButtonStyle("deals")}
                />
                <FooterButton
                  id="settings-drawer"
                  icon={<MdSettings size={20} color="#fff" />}
                  onClick={() => openDealsDrawer("settings")}
                  onHoverChange={(isHovered) =>
                    setHoveredFooterButton(isHovered ? "settings" : null)
                  }
                  style={getFooterButtonStyle("settings")}
                />
              </>
            )}

            {/* Always visible buttons - show on both deals and worksheet pages */}
            <FooterButton
              id="print-drawer"
              icon={<MdPrint size={20} color="#fff" />}
              onClick={() => openDealsDrawer("print")}
              onHoverChange={(isHovered) =>
                setHoveredFooterButton(isHovered ? "print" : null)
              }
              style={getFooterButtonStyle("print")}
            />
            <FooterButton
              id="send-drawer"
              icon={<MdSend size={20} color="#fff" />}
              onClick={() => openDealsDrawer("send")}
              onHoverChange={(isHovered) =>
                setHoveredFooterButton(isHovered ? "send" : null)
              }
              style={getFooterButtonStyle("send")}
            />
            <FooterButton
              id="more"
              icon={<MdMoreHoriz size={25} color="#fff" />}
              onClick={() => openDealsDrawer("more")}
              onHoverChange={(isHovered) =>
                setHoveredFooterButton(isHovered ? "more" : null)
              }
              style={getFooterButtonStyle("more")}
            />

            {hasWorksheetParam && account?.permissions?.deals_worksheets_delete && (
              <FooterButton
                id="remove"
                className="customer-remove-btn"
                icon={<MdDelete size={18} color="#fff" />}
                onClick={handleRemoveClick}
                onHoverChange={(isHovered) =>
                  setHoveredFooterButton(isHovered ? "remove" : null)
                }
                style={{
                  ...getFooterButtonStyle("remove"),
                  backgroundColor: activeColorStyle.borderColor,
                  borderColor: activeColorStyle.borderColor,
                }}
                iconColor="#fff"
              />
            )}
          </>
        )}
        <FooterButton
          id="actions-toggle"
          icon={
            <i
              className={`tim-icons  ${actionsClosed ? "icon-minimal-up" : "icon-minimal-down"
                }`}
            />
          }
          onHoverChange={(isHovered) =>
            setHoveredFooterButton(isHovered ? "actions-toggle" : null)
          }
          onClick={() => setActionsClosed(!actionsClosed)}
          style={getFooterButtonStyle("actions-toggle")}
          iconColor={"#fff"}
        />
      </div>

      <Modal isOpen={deleteWorksheetModalOpen} toggle={handleCancelWorksheetDelete} centered={false} style={{ marginTop: '100px' }}>
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelWorksheetDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t('dealWorksheetsTab.deleteWorksheet')}</h6>
        </div>
        <ModalBody className="text-center">
          <p>{t('dealWorksheetsTab.areYouSureDeleteWorksheet')}</p>
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="secondary"
            type="button"
            onClick={handleCancelWorksheetDelete}
            disabled={deleteWorksheetMutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            style={{
              background: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              backgroundImage: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              borderColor: '#fd5d93',
              color: 'white'
            }}
            color="danger"
            type="button"
            onClick={handleConfirmWorksheetDelete}
            disabled={deleteWorksheetMutation.isPending}
          >
            {deleteWorksheetMutation.isPending ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                {t('common.deleting')}
              </>
            ) : (
              t('dealApplicantTab.yesDelete')
            )}
          </Button>
        </div>
      </Modal>

      <DealsRightDrawer
        ref={dealsDrawerRef}
        activeColorStyle={activeColorStyle}
        activeColor={activeColor}
        worksheetId={worksheetId || undefined}
        isWorksheet={hasWorksheetParam}
        applicantData={{ ...dealMainData?.customer, ...dealMainData?.deals_applicant, ...dealMainData?.wholesaler }}
        dealId={id}
        onNavigateToWorksheets={navigateToWorksheetsTab}
        onAddTradeIn={handleAddTradeIn}
        hasTradeIn1={!!dealMainData?.trade_in_1 || tabs.includes("TRADE-IN-1")}
        hasTradeIn2={!!dealMainData?.trade_in_2 || tabs.includes("TRADE-IN-2")}
        onRemoveTradeIn1={handleRemoveTradeIn1}
        onAddTradeIn2={handleAddTradeIn2}
        onRemoveTradeIn2={handleRemoveTradeIn2}
        onAddDesiredVehicle={handleAddDesiredVehicle}
        onRemoveDesiredVehicle={handleRemoveDesiredVehicle}
        hasDesiredVehicle={!!dealMainData?.desired_vehicle}
        contractSummary={contractDealSummary}
        decisionHistory={decisionHistory}
        shellThemeVariant={shellThemeVariant}
      />

      <Modal
        isOpen={deleteModalOpen}
        toggle={handleCancelDelete}
        centered={false}
        style={{ marginTop: "100px" }}
      >
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t("dealsDetail.deleteDeal")}</h6>
        </div>
        <ModalBody className="text-center">
          <p>{t("dealsDetail.areYouSureDeleteDeal")}</p>
          <p className="text-muted">
            {t("dealsDetail.thisActionCannotBeUndone")}
          </p>
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="secondary"
            type="button"
            onClick={handleCancelDelete}
            disabled={deleteDealMutation.isPending}
          >
            {t("dealsDetail.cancel")}
          </Button>
          <Button
            style={{
              background:
                "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              backgroundImage:
                "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              borderColor: "#fd5d93",
              color: "white",
            }}
            color="danger"
            type="button"
            onClick={handleConfirmDelete}
            disabled={deleteDealMutation.isPending}
          >
            {deleteDealMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm mr-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {t("dealsDetail.deleting")}
              </>
            ) : (
              t("dealsDetail.yesDelete")
            )}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default DealsDetail;
